import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSession } from '../../contexts/SessionContext';
import { CindroBrand } from '../../components/CindroBrand';

type AuthPageProps = {
  initialMode: 'login' | 'signup';
};

function AuthPage({ initialMode }: AuthPageProps) {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [authMode, setAuthMode] = useState<'login' | 'signup'>(initialMode);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authDisplayName, setAuthDisplayName] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');

  async function handleAuthSubmit(event: React.FormEvent<HTMLFormElement>) {
    setAuthBusy(true);
    setAuthError('');
    try {
      await signIn(event, {
        authMode,
        authEmail,
        authPassword,
        authDisplayName,
        locationSearch: location.search,
        onRedirect: (path) => navigate(path, { replace: true }),
      });
    } catch (error) {
      setAuthError(
        error instanceof Error ? error.message : 'Unable to authenticate.'
      );
    } finally {
      setAuthBusy(false);
    }
  }

  return (
    <div className="authShell">
      <form className="authCard" onSubmit={handleAuthSubmit}>
        <CindroBrand />
        <h1>{authMode === 'login' ? 'Sign in' : 'Create account'}</h1>
        <p>
          Ship native updates with an exact, auditable release trail.
        </p>

        <div className="authTabs">
          <button
            type="button"
            className={authMode === 'login' ? 'authTab active' : 'authTab'}
            onClick={() => setAuthMode('login')}
          >
            Login
          </button>
          <button
            type="button"
            className={authMode === 'signup' ? 'authTab active' : 'authTab'}
            onClick={() => setAuthMode('signup')}
          >
            Signup
          </button>
        </div>

        {authMode === 'signup' ? (
          <label className="authField">
            <span>Display name</span>
            <input
              value={authDisplayName}
              onChange={(event) => setAuthDisplayName(event.target.value)}
              placeholder="Sarah Kim"
            />
          </label>
        ) : null}

        <label className="authField">
          <span>Email</span>
          <input
            type="email"
            value={authEmail}
            onChange={(event) => setAuthEmail(event.target.value)}
            placeholder="sarah@acme.com"
          />
        </label>

        <label className="authField">
          <span>Password</span>
          <input
            type="password"
            value={authPassword}
            onChange={(event) => setAuthPassword(event.target.value)}
            placeholder="••••••••"
          />
        </label>

        {authMode === 'login' ? (
          <div style={{ textAlign: 'right', marginTop: '-0.25rem' }}>
            <Link
              to="/forgot-password"
              style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted, #6b7280)', textDecoration: 'none' }}
            >
              Forgot password?
            </Link>
          </div>
        ) : null}

        {authError ? <div className="authError">{authError}</div> : null}

        <button
          type="submit"
          className="primaryButton authSubmit"
          disabled={authBusy}
        >
          {authBusy
            ? 'Working…'
            : authMode === 'login'
              ? 'Sign in'
              : 'Create account'}
        </button>
      </form>
    </div>
  );
}

export { AuthPage };
